import React, { useState, useEffect, useCallback } from 'react'
import { Search, Share2, Copy, MessageCircle, Download, Tag, ChevronDown, ChevronRight, AlertTriangle, User, RefreshCw } from 'lucide-react'
import {
  Button, Input, Card, Badge,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../ui'
import {
  getFamilias, getCargosByJugador, getCargosByFamilia, getAllPlayersWithGroups,
  getSaldoFavor, aplicarSaldoFavorACargo, retirarSaldoFavor, anularPago,
  getCobranzaPendiente, getPromocionesAplicables, asignarPromocion,
  getEstadoEntregables, regenerarPinFamilia, desbloquearFamilia, crearCuentaParaJugador,
  regenerarTokenFamilia,
} from '../../../lib/supabase'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Label, DialogFooter } from '../../ui'
import { exportEstadoCuentaExcel } from '../../../utils/pagosExport'
import { nombreFamilia } from '../../../lib/familias'
import { isVisiblePlayer } from '../../../lib/playerVisibility'
import { formatDate, formatMoney } from '../../../utils/helpers'
import { useUnsavedGuard } from '../../../hooks/useUnsavedGuard'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { useInscritosTemporada } from '../../../hooks/useInscritosTemporada'
import { FiltroInscritos, AvisoSinPadron } from '../FiltroInscritos'

const ESTATUS_COLORS = {
  pendiente: 'bg-amber-100 text-amber-800',
  parcial: 'bg-blue-100 text-blue-800',
  pagado: 'bg-green-100 text-green-800',
  vencido: 'bg-red-100 text-red-800',
}

const METODO_LABELS = { efectivo: 'Efectivo', transferencia: 'Transferencia', tarjeta: 'Tarjeta', saldo_favor: 'Saldo a favor', otro: 'Otro' }

const CargosTable = ({ cargos, saldosFavor = [] }) => {
  const [expanded, setExpanded] = useState({})
  const toggle = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-6"></TableHead>
          <TableHead>Concepto</TableHead>
          <TableHead>Monto</TableHead>
          <TableHead>Pagado</TableHead>
          <TableHead>Saldo</TableHead>
          <TableHead>Vencimiento</TableHead>
          <TableHead>Estatus</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {cargos.map((c) => {
          const tieneDescuento = c.descuento_porcentaje > 0 || c.descuento_monto > 0
          const pagos = (c.pagos_transacciones_detalle || [])
            .filter((d) => d.pagos_transacciones && !d.pagos_transacciones.anulado)
          const tienePagos = pagos.length > 0
          const isExpanded = expanded[c.id]
          const conRetraso = c.estatus !== 'pagado' && c.estatus !== 'cancelado' && c.fecha_vencimiento && new Date(c.fecha_vencimiento + 'T23:59:59') < new Date()

          return (
            <React.Fragment key={c.id}>
              <TableRow
                className={tienePagos ? 'cursor-pointer hover:bg-muted/50' : ''}
                onClick={() => tienePagos && toggle(c.id)}
              >
                <TableCell className="w-6 px-2">
                  {tienePagos && (
                    isExpanded
                      ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </TableCell>
                <TableCell className="text-sm font-medium">
                  {c.pagos_planes_parcialidades?.nombre || c.pagos_conceptos?.nombre || c.notas || 'cargo'}
                  {c.pagos_planes?.nombre && (
                    <p className="text-[10px] text-muted-foreground">{c.pagos_planes.nombre}</p>
                  )}
                  {tieneDescuento && (
                    <p className="text-[10px] text-emerald-600">
                      {c.pagos_promociones?.nombre ? `${c.pagos_promociones.nombre} (-${c.descuento_porcentaje}%)` : `-${c.descuento_porcentaje}%`}
                    </p>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {tieneDescuento ? (
                    <div>
                      <span className="line-through text-muted-foreground text-xs">{formatMoney(c.monto_original)}</span>
                      <p className="font-medium">{formatMoney(c.monto_final)}</p>
                    </div>
                  ) : (
                    <span className="font-medium">{formatMoney(c.monto_final)}</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">{formatMoney(c.monto_pagado)}</TableCell>
                <TableCell className="text-sm font-medium">{formatMoney(c.saldo)}</TableCell>
                <TableCell className="text-sm">{formatDate(c.fecha_vencimiento)}</TableCell>
                <TableCell>
                  <Badge className={`${conRetraso ? 'bg-red-100 text-red-800' : ESTATUS_COLORS[c.estatus] || ''} text-xs`}>
                    {conRetraso && c.estatus === 'parcial' ? 'Parcial · Retraso' : conRetraso && c.estatus === 'pendiente' ? 'Vencido' : c.estatus === 'vencido' ? 'Vencido' : c.estatus.charAt(0).toUpperCase() + c.estatus.slice(1)}
                  </Badge>
                </TableCell>
              </TableRow>
              {isExpanded && pagos.map((d) => (
                <TableRow key={d.id} className="bg-muted/20">
                  <TableCell className="w-6 px-2"></TableCell>
                  <TableCell className="py-1.5">
                    <div className="border-l-2 border-emerald-300 pl-3 text-xs text-muted-foreground">
                      <span className="font-medium text-foreground">{formatDate(d.pagos_transacciones?.fecha_pago)}</span>
                      <Badge variant="outline" className="text-[9px] ml-1.5 px-1.5 py-0">
                        {METODO_LABELS[d.pagos_transacciones?.metodo_pago] || d.pagos_transacciones?.metodo_pago}
                      </Badge>
                      {d.pagos_transacciones?.referencia && (
                        <span className="ml-1.5 text-muted-foreground/70">{d.pagos_transacciones.referencia}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-xs font-semibold text-emerald-700 py-1.5">+{formatMoney(d.monto_aplicado)}</TableCell>
                  <TableCell colSpan={3}></TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          )
        })}
      </TableBody>
    </Table>
  )
}

// Un cargo como renglon: el concepto a la izquierda, el importe a la derecha en
// tabular y debajo el vencimiento con su estado. Evita meter una tabla de 7
// columnas dentro de una fila ya indentada en telefono.
const CargoRenglon = ({ cargo }) => {
  const tieneDescuento = cargo.descuento_porcentaje > 0 || cargo.descuento_monto > 0
  const conRetraso = cargo.estatus !== 'pagado' && cargo.estatus !== 'cancelado' &&
    cargo.fecha_vencimiento && new Date(cargo.fecha_vencimiento + 'T23:59:59') < new Date()
  const saldo = Number(cargo.saldo || 0)

  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="text-sm truncate">
          {cargo.pagos_planes_parcialidades?.nombre || cargo.pagos_conceptos?.nombre || cargo.notas || 'Cargo'}
        </p>
        <p className="text-xs text-muted-foreground">
          {cargo.fecha_vencimiento ? `Vence ${formatDate(cargo.fecha_vencimiento)}` : 'Sin fecha'}
          {' · '}
          <span className={conRetraso ? 'text-red-600 font-medium' : saldo <= 0 ? 'text-green-700' : ''}>
            {conRetraso ? 'Vencido' : saldo <= 0 ? 'Pagado' : `Debe ${formatMoney(saldo)}`}
          </span>
        </p>
        {tieneDescuento && (
          <p className="text-[11px] text-emerald-600">
            {cargo.pagos_promociones?.nombre
              ? `${cargo.pagos_promociones.nombre} (-${cargo.descuento_porcentaje}%)`
              : `-${cargo.descuento_porcentaje}%`}
          </p>
        )}
      </div>
      <div className="shrink-0 text-right">
        {tieneDescuento && (
          <p className="text-[11px] text-muted-foreground line-through tabular-nums">
            {formatMoney(cargo.monto_original)}
          </p>
        )}
        <p className="text-sm font-semibold tabular-nums">{formatMoney(cargo.monto_final)}</p>
      </div>
    </div>
  )
}

const JugadoresDetalle = ({ jugadores, onDescuento, onAplicarSaldo, onRetirarSaldo, onDeshacerAplicacion }) => (
  <div className="space-y-4">
    {jugadores.map((j) => (
      <div key={j.id} className="space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-sm font-medium truncate">{j.nombre}</span>
            {j.numero && <Badge variant="outline" className="text-[10px] shrink-0">#{j.numero}</Badge>}
          </div>
          <div className="shrink-0 text-right">
            <p className={`text-sm font-semibold tabular-nums ${Number(j.saldo) > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
              {formatMoney(j.saldo)}
            </p>
            <p className="text-[11px] text-muted-foreground">Saldo</p>
          </div>
        </div>

        {j.cargos.length > 0 ? (
          <>
            {/* Movil: renglones. Escritorio: la tabla completa con Pagado y Estatus. */}
            <Card className="sm:hidden divide-y px-3">
              {j.cargos.map((c) => <CargoRenglon key={c.id} cargo={c} />)}
            </Card>
            <Card className="hidden sm:block"><CargosTable cargos={j.cargos} /></Card>
          </>
        ) : (
          <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
            Sin cargos para esta temporada.
          </p>
        )}

        {/* Dinero disponible a su favor. Va ANTES de las acciones porque cambia
            lo que el admin decide hacer: cobrar o aplicar lo que ya tiene. */}
        {(j.saldoFavor > 0 || j.aplicaciones?.length > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
            <span className="text-sm text-emerald-900">
              Saldo a favor disponible: <strong className="tabular-nums">{formatMoney(j.saldoFavor)}</strong>
            </span>
            {/* Aplicarlo es lo que casi siempre se quiere; retirarlo es el
                remedio cuando el saldo esta mal -- un sobrante de un pago que no
                debio existir, por ejemplo -- y por eso va como secundario. */}
            <div className="flex items-center gap-2">
              {j.saldoFavor > 0 && j.cargos.some((c) => Number(c.saldo) > 0) && (
                <Button size="sm" className="h-8 text-xs" onClick={() => onAplicarSaldo(j)}>
                  Aplicar a un cargo
                </Button>
              )}
              {j.saldoFavor > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => onRetirarSaldo(j)}
                >
                  Retirar
                </Button>
              )}
            </div>

            {/* A que cargos se aplico. Aplicarlo al cargo equivocado era un camino
                sin retorno: el cargo quedaba pagado, el credito consumido y
                ninguna pantalla ofrecia revertirlo. */}
            {j.aplicaciones?.length > 0 && (
              <ul className="w-full space-y-1 border-t border-emerald-200 pt-2">
                {j.aplicaciones.map((ap) => (
                  <li key={ap.id} className="flex items-center justify-between gap-2 text-xs text-emerald-900">
                    <span className="min-w-0 truncate">
                      Aplicado <strong className="tabular-nums">{formatMoney(ap.monto_aplicado)}</strong> a {ap.concepto}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 shrink-0 text-xs"
                      disabled={!ap.transaccion_id}
                      title={ap.transaccion_id
                        ? 'Devuelve el saldo y reabre el cargo'
                        : 'Esta aplicación es anterior al registro de su pago y no se puede deshacer sola'}
                      onClick={() => onDeshacerAplicacion(j, ap)}
                    >
                      Deshacer
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Acciones al final: son secundarias frente a leer el estado de cuenta */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => onDescuento(j.id, j.nombre)}>
            <Tag className="mr-1.5 h-3.5 w-3.5" /> Descuento
          </Button>
          {j.cargos.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => exportEstadoCuentaExcel(j.cargos, { nombre: j.nombre, tipo: 'jugador' })}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> Excel
            </Button>
          )}
        </div>
        {j.entregables?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {j.entregables.map((e, i) => (
              <Badge key={i} className={`text-xs ${e.cubierto ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                {e.nombre}: {e.cubierto ? 'Listo' : `faltan ${formatMoney(e.falta_monto)}`}
              </Badge>
            ))}
          </div>
        )}
      </div>
    ))}
  </div>
)

const EstadoCuentaTab = ({ temporadaId, temporadaSelector = null }) => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const inscritosEstado = useInscritosTemporada(temporadaId)
  const [familias, setFamilias] = useState([])
  const [players, setPlayers] = useState([])
  const [saldoFamilia, setSaldoFamilia] = useState({})
  const [saldoJugador, setSaldoJugador] = useState({})
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [detalle, setDetalle] = useState({})
  const [loadingId, setLoadingId] = useState(null)

  // Compartir
  const [shareOpen, setShareOpen] = useState(false)
  const [shareFamilia, setShareFamilia] = useState(null)
  const [generatedPin, setGeneratedPin] = useState(null)
  const [regenPin, setRegenPin] = useState(false)
  const [pinCopied, setPinCopied] = useState(false)
  const [revocandoLink, setRevocandoLink] = useState(false)
  const [unlocking, setUnlocking] = useState(false)
  const [copied, setCopied] = useState(false)
  const [creatingShareId, setCreatingShareId] = useState(null)

  // Descuento
  const [promoDialog, setPromoDialog] = useState({ open: false, jugadorId: null, jugadorNombre: '', contextKey: null })
  const guard = useUnsavedGuard({ onClose: () => setPromoDialog({ open: false, jugadorId: null, jugadorNombre: '', contextKey: null }) })
  const [promosAplicables, setPromosAplicables] = useState([])
  const [selectedPromoId, setSelectedPromoId] = useState('')
  const [assigningPromo, setAssigningPromo] = useState(false)

  const loadOverview = useCallback(async () => {
    const [familiasRes, playersRes, cobranzaRes] = await Promise.all([
      getFamilias(),
      getAllPlayersWithGroups(),
      getCobranzaPendiente(temporadaId),
    ])
    setFamilias(familiasRes.data || [])
    setPlayers((playersRes.data || []).filter(isVisiblePlayer))
    const sf = {}, sj = {}
    const cob = cobranzaRes.data || { familias: [], jugadores: [] }
    ;(cob.familias || []).forEach((e) => { if (e.familia?.id) sf[e.familia.id] = e.saldo })
    ;(cob.jugadores || []).forEach((e) => { if (e.jugador?.id) sj[e.jugador.id] = e.saldo })
    setSaldoFamilia(sf)
    setSaldoJugador(sj)
  }, [temporadaId])

  useEffect(() => { loadOverview() }, [loadOverview])

  const buildDetalle = async (cargos, jugadoresHint) => {
    const byJ = {}
    ;(cargos || []).forEach((c) => {
      const jId = c.jugador_id || 'sin'
      if (!byJ[jId]) byJ[jId] = { id: jId, nombre: c.jugadores?.nombre_completo || 'Jugador', numero: c.jugadores?.numero, cargos: [] }
      byJ[jId].cargos.push(c)
    })
    ;(jugadoresHint || []).forEach((j) => {
      if (!byJ[j.id]) byJ[j.id] = { id: j.id, nombre: j.nombre_completo, numero: j.numero, cargos: [] }
    })
    // Un jugador que ya no participa este ciclo puede arrastrar cargos viejos; si
    // no se filtra aqui, se cuela por la puerta de atras al abrir la cuenta.
    const jugadores = inscritosEstado.filtrar(Object.values(byJ))
    await Promise.all(jugadores.map(async (j) => {
      j.saldo = j.cargos.reduce((s, c) => s + Number(c.saldo || 0), 0)
      // Saldo a favor disponible: sin esto, una beca queda invisible justo en la
      // pantalla donde se consulta cuanto debe la familia.
      // Sin `soloDisponible: false` un saldo aplicado por completo desaparece --
      // y es justo el que hay que poder deshacer.
      const { data: saldos } = await getSaldoFavor(j.id, { soloDisponible: false })
      j.saldos = saldos || []
      j.saldoFavor = (saldos || []).reduce((t, x) => t + Number(x.saldo_disponible || 0), 0)
      j.aplicaciones = (saldos || []).flatMap((sf) =>
        (sf.pagos_saldo_favor_aplicaciones || []).map((ap) => ({
          ...ap,
          saldoId: sf.id,
          concepto: ap.pagos_cargos?.pagos_conceptos?.nombre || 'un cargo',
        })))
      const planIds = [...new Set(j.cargos.map((c) => c.plan_id).filter(Boolean))]
      if (planIds.length) {
        const res = await Promise.all(planIds.map((pid) => getEstadoEntregables(j.id, pid)))
        j.entregables = res.flatMap((r) => r.data || [])
      } else {
        j.entregables = []
      }
    }))
    return jugadores
  }

  const loadFamiliaJugadores = useCallback(async (fam) => {
    const { data } = await getCargosByFamilia(fam.id, temporadaId)
    return buildDetalle(data, fam.jugadores)
  }, [temporadaId, inscritosEstado]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadOrphanJugadores = useCallback(async (p) => {
    const { data } = await getCargosByJugador(p.id, temporadaId)
    return buildDetalle(data, [{ id: p.id, nombre_completo: p.nombre_completo, numero: p.numero }])
  }, [temporadaId, inscritosEstado]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (key, loader) => {
    if (expandedId === key) { setExpandedId(null); return }
    setExpandedId(key)
    if (!detalle[key]) {
      setLoadingId(key)
      const jugadores = await loader()
      setDetalle((prev) => ({ ...prev, [key]: jugadores }))
      setLoadingId(null)
    }
  }

  const reloadDetalle = async (key) => {
    const fam = familias.find((f) => f.id === key)
    let jugadores
    if (fam) {
      jugadores = await loadFamiliaJugadores(fam)
    } else {
      const p = players.find((x) => `orphan_${x.id}` === key)
      if (!p) return
      jugadores = await loadOrphanJugadores(p)
    }
    setDetalle((prev) => ({ ...prev, [key]: jugadores }))
  }

  const [saldoDialog, setSaldoDialog] = useState({ open: false, jugador: null, contextKey: null })
  const [cargoElegido, setCargoElegido] = useState('')
  const [montoAplicar, setMontoAplicar] = useState('')
  const [aplicandoSaldo, setAplicandoSaldo] = useState(false)

  // Retirar el saldo que no debio existir. Se pregunta con los numeros delante y
  // se dice en voz alta lo que NO se deshace: lo ya aplicado cerro cargos, y
  // revertirlo reabriria deudas que la familia da por pagadas.
  const handleRetirarSaldo = async (jugador, contextKey) => {
    const vivos = (jugador.saldos || []).filter((s) => Number(s.saldo_disponible || 0) > 0)
    const disponible = vivos.reduce((t, s) => t + Number(s.saldo_disponible || 0), 0)
    const aplicado = (jugador.saldos || []).reduce((t, s) => t + Number(s.monto_usado || 0), 0)
    if (!vivos.length) {
      await alert('Sin saldo por retirar', `${jugador.nombre} no tiene saldo disponible.`)
      return
    }

    const ok = await confirm(
      'Retirar saldo a favor',
      `Se le retirarán ${formatMoney(disponible)} a ${jugador.nombre}.`
      + (aplicado > 0
        ? `\n\nLos ${formatMoney(aplicado)} que ya se aplicaron a sus cargos NO se revierten: ese dinero cerró adeudos.`
        : '')
      + '\n\n¿Continuar?',
      { destructive: true },
    )
    if (!ok) return

    let retirado = 0
    for (const s of vivos) {
      const { data, error } = await retirarSaldoFavor(s.id, { motivo: 'retirado por el club' })
      if (error) { await alert('Error', error.message || 'No se pudo retirar el saldo.'); return }
      retirado += Number(data?.retirado || 0)
    }
    if (contextKey) await reloadDetalle(contextKey)
    loadOverview()
    await alert('Saldo retirado', `Se retiraron ${formatMoney(retirado)}.`)
  }

  // Deshacer una aplicacion de saldo: el cargo se reabre y el credito vuelve.
  // Aplicarlo al cargo equivocado era un camino sin retorno hasta 2026-09-02.
  const handleDeshacerAplicacion = async (jugador, ap, contextKey) => {
    if (!ap.transaccion_id) return
    const ok = await confirm(
      'Deshacer aplicación',
      `Se le devolverán ${formatMoney(ap.monto_aplicado)} a ${jugador.nombre} y ${ap.concepto} volverá a quedar pendiente.`
      + '\n\n¿Continuar?',
      { destructive: true },
    )
    if (!ok) return

    const { error } = await anularPago(ap.transaccion_id, 'aplicación de saldo deshecha')
    if (error) { await alert('Error', error.message || 'No se pudo deshacer.'); return }
    if (contextKey) await reloadDetalle(contextKey)
    loadOverview()
    await alert('Listo', `Se devolvieron ${formatMoney(ap.monto_aplicado)} al saldo a favor.`)
  }

  const openSaldoDialog = (jugador, contextKey) => {
    setCargoElegido('')
    setMontoAplicar('')
    setSaldoDialog({ open: true, jugador, contextKey })
  }

  // Se consumen los saldos mas viejos primero, igual que en Pagos: si alguien tiene
  // una beca y un pago a cuenta, no importa de cual salga -- importa que el cargo
  // quede cubierto y que ningun saldo se pierda.
  const confirmarAplicarSaldo = async () => {
    const j = saldoDialog.jugador
    const monto = Number(montoAplicar)
    if (!j || !cargoElegido || !(monto > 0)) return
    setAplicandoSaldo(true)
    let restante = monto
    for (const saldo of (j.saldos || [])) {
      if (restante <= 0) break
      const disponible = Number(saldo.saldo_disponible || 0)
      if (disponible <= 0) continue
      const aplicar = Math.min(restante, disponible)
      const { error } = await aplicarSaldoFavorACargo(saldo.id, cargoElegido, aplicar)
      if (error) {
        setAplicandoSaldo(false)
        await alert('No se pudo aplicar', error.message || 'Error al aplicar el saldo')
        return
      }
      restante = Math.round((restante - aplicar) * 100) / 100
    }
    setAplicandoSaldo(false)
    const key = saldoDialog.contextKey
    setSaldoDialog({ open: false, jugador: null, contextKey: null })
    if (key) await reloadDetalle(key)
    loadOverview()
  }

  const openPromoDialog = async (jugadorId, jugadorNombre, contextKey) => {
    setPromoDialog({ open: true, jugadorId, jugadorNombre, contextKey })
    setSelectedPromoId('')
    guard.resetDirty()
    const { data } = await getPromocionesAplicables(jugadorId, temporadaId)
    setPromosAplicables(data || [])
  }

  const handleAsignarPromo = async () => {
    if (!selectedPromoId || !promoDialog.jugadorId) return
    setAssigningPromo(true)
    // Mismo servicio que la pantalla de Asignaciones. Antes esta pantalla tenia su
    // propio camino, que ignoraba el descuento de hermanos y la temporada.
    await asignarPromocion({ promocionId: selectedPromoId, jugadorIds: [promoDialog.jugadorId] })
    setAssigningPromo(false)
    guard.resetDirty()
    const key = promoDialog.contextKey
    setPromoDialog({ open: false, jugadorId: null, jugadorNombre: '', contextKey: null })
    if (key) await reloadDetalle(key)
    loadOverview()
  }

  const openShare = (familia) => {
    setShareFamilia(familia)
    setGeneratedPin(null)
    setPinCopied(false)
    setShareOpen(true)
  }

  // Red de seguridad: toda ficha deberia tener familia (se crea sola al registrar y
  // se relleno el historico). Si alguna se cuela sin ella, desde aqui se le crea.
  const handleCrearYCompartir = async (player) => {
    setCreatingShareId(player.id)
    const { data: cuenta, error } = await crearCuentaParaJugador(player)
    setCreatingShareId(null)
    if (error || !cuenta) return
    await loadOverview()
    setShareFamilia(cuenta)
    setGeneratedPin(cuenta.pin_plain || null)
    setPinCopied(false)
    setShareOpen(true)
  }

  const handleGenerarPin = async () => {
    if (!shareFamilia) return
    setRegenPin(true)
    const { data, error } = await regenerarPinFamilia(shareFamilia.id)
    setRegenPin(false)
    if (!error && data?.pin_plain) setGeneratedPin(data.pin_plain)
  }

  const copyPin = () => {
    if (!generatedPin) return
    navigator.clipboard.writeText(generatedPin)
    setPinCopied(true)
    setTimeout(() => setPinCopied(false), 2000)
  }

  // Revocar el link: si se filtro (grupo de WhatsApp, telefono prestado), cualquiera
  // con la direccion llega a la pantalla de PIN. Cambiar el token corta ese acceso --
  // y de paso invalida el link que ya tenia el tutor, que hay que volverle a mandar.
  const handleRevocarLink = async () => {
    if (!shareFamilia) return
    const ok = await confirm(
      'Revocar el link actual',
      `El link que ya tiene ${shareFamilia.responsable?.nombre_completo || 'el responsable'} dejará de funcionar. Tendrás que enviarle el nuevo.`,
      { destructive: true }
    )
    if (!ok) return
    setRevocandoLink(true)
    const { data, error } = await regenerarTokenFamilia(shareFamilia.id)
    setRevocandoLink(false)
    if (error) { await alert('Error', error.message || 'No se pudo revocar el link'); return }
    setShareFamilia((f) => ({ ...f, access_token: data.access_token }))
    setCopied(false)
    loadOverview()
  }

  const handleDesbloquear = async () => {
    if (!shareFamilia) return
    setUnlocking(true)
    const { error } = await desbloquearFamilia(shareFamilia.id)
    setUnlocking(false)
    if (!error) setShareFamilia((f) => ({ ...f, pin_intentos: 0, pin_bloqueado_hasta: null }))
  }

  const getShareUrl = (familia) => `${window.location.origin}/cuenta/${familia.access_token}`

  const copyLink = (familia) => {
    navigator.clipboard.writeText(getShareUrl(familia))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const shareWhatsApp = (familia) => {
    const url = getShareUrl(familia)
    const tel = (familia.responsable?.telefono || '').replace(/\D/g, '')
    const msg = encodeURIComponent(`Hola ${familia.responsable?.nombre_completo || ''}, aqui puedes ver el estado de cuenta de tu familia en el club:\n${url}\n\nNecesitaras tu PIN de acceso.`)
    window.open(tel ? `https://wa.me/52${tel}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank')
  }

  // Búsqueda y huérfanos. El padrón manda: una cuenta se muestra si tiene al
  // menos un jugador inscrito en la temporada, y adentro solo salen los inscritos.
  const term = searchTerm.toLowerCase().trim()
  const jugadoresDeFamilia = (f) => inscritosEstado.filtrar(f.jugadores || [])
  const filteredFamilias = familias.filter((f) =>
    jugadoresDeFamilia(f).length > 0
    && (!term
      || f.nombre?.toLowerCase().includes(term)
      || f.responsable?.nombre_completo?.toLowerCase().includes(term)
      || jugadoresDeFamilia(f).some((j) => j.nombre_completo?.toLowerCase().includes(term)))
  )
  const playersInFamilia = new Set(familias.flatMap((f) => (f.jugadores || []).map((j) => j.id)))
  const filteredOrphans = inscritosEstado.filtrar(players).filter((p) =>
    !playersInFamilia.has(p.id)
    && (!term || p.nombre_completo?.toLowerCase().includes(term) || p.numero?.toString().includes(term))
  )

  // Columna de saldo: la cifra alineada a la derecha y en tabular-nums permite
  // comparar deudas recorriendo una sola columna, que es para lo que se abre
  // esta pantalla. El estado va debajo, en chico.
  const SaldoColumna = ({ saldo }) => (
    <div className="shrink-0 text-right">
      <p className={`text-sm font-semibold tabular-nums ${saldo > 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
        {formatMoney(saldo)}
      </p>
      <p className={`text-[11px] ${saldo > 0 ? 'text-amber-700' : 'text-green-700'}`}>
        {saldo > 0 ? 'Debe' : 'Al corriente'}
      </p>
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      {temporadaSelector && <div className="min-w-0">{temporadaSelector}</div>}
      <div className="flex items-center gap-2 flex-nowrap">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar familia, tutor o jugador..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <FiltroInscritos estado={inscritosEstado} id="ec-ver-no-inscritos" />
      </div>
      <AvisoSinPadron estado={inscritosEstado} />

      {/* Lista */}
      <div className="space-y-2">
        {filteredFamilias.length === 0 && filteredOrphans.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">No se encontraron resultados.</p>
        )}

        {filteredFamilias.map((fam) => {
          const isOpen = expandedId === fam.id
          const jugadores = detalle[fam.id]
          return (
            <Card key={fam.id}>
              <div className="flex items-start gap-2 p-3">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-start gap-2 text-left"
                  onClick={() => toggle(fam.id, () => loadFamiliaJugadores(fam))}
                  aria-expanded={isOpen}
                >
                  <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  <span className="min-w-0">
                    <span className="block font-medium truncate">{nombreFamilia(fam)}</span>
                    {/* El titulo ya es el responsable, asi que repetirlo aqui era decir
                        dos veces lo mismo. Lo util debajo es a quien cubre. */}
                    <span className="block text-xs text-muted-foreground truncate">
                      {jugadoresDeFamilia(fam).map((j) => j.nombre_completo).filter(Boolean).join(', ') || 'Sin jugadores'}
                    </span>
                  </span>
                </button>
                <SaldoColumna saldo={saldoFamilia[fam.id] || 0} />
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 h-8 w-8 p-0 sm:h-8 sm:w-auto sm:px-3"
                  onClick={() => openShare(fam)}
                  aria-label="Compartir acceso"
                  title="Compartir acceso"
                >
                  <Share2 className="h-3.5 w-3.5 sm:mr-1.5" />
                  <span className="hidden sm:inline">Compartir</span>
                </Button>
              </div>
              {isOpen && (
                <div className="border-t p-3">
                  {loadingId === fam.id || !jugadores ? (
                    <p className="py-2 text-center text-sm text-muted-foreground">Cargando...</p>
                  ) : (
                    <JugadoresDetalle jugadores={jugadores} onDescuento={(jId, nombre) => openPromoDialog(jId, nombre, fam.id)} onAplicarSaldo={(j) => openSaldoDialog(j, fam.id)} onRetirarSaldo={(j) => handleRetirarSaldo(j, fam.id)} onDeshacerAplicacion={(j, ap) => handleDeshacerAplicacion(j, ap, fam.id)} />
                  )}
                </div>
              )}
            </Card>
          )
        })}

        {filteredOrphans.length > 0 && (
          <>
            <p className="px-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sin familia</p>
            {filteredOrphans.map((p) => {
              const key = `orphan_${p.id}`
              const isOpen = expandedId === key
              const jugadores = detalle[key]
              return (
                <Card key={key}>
                  <div className="flex items-start gap-2 p-3">
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                      onClick={() => toggle(key, () => loadOrphanJugadores(p))}
                      aria-expanded={isOpen}
                    >
                      <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      <span className="min-w-0">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate">{p.nombre_completo}</span>
                          {p.numero && <Badge variant="outline" className="text-xs shrink-0">#{p.numero}</Badge>}
                        </span>
                        <span className="block text-xs text-muted-foreground">Sin familia asignada</span>
                      </span>
                    </button>
                    <SaldoColumna saldo={saldoJugador[p.id] || 0} />
                    <Button
                      variant="outline"
                      size="sm"
                      className="shrink-0 h-8 w-8 p-0 sm:h-8 sm:w-auto sm:px-3"
                      onClick={() => handleCrearYCompartir(p)}
                      disabled={creatingShareId === p.id}
                      aria-label="Crear familia y compartir acceso"
                      title="Crea su familia y comparte el acceso"
                    >
                      <Share2 className="h-3.5 w-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">{creatingShareId === p.id ? 'Creando...' : 'Compartir'}</span>
                    </Button>
                  </div>
                  {isOpen && (
                    <div className="border-t p-3">
                      {loadingId === key || !jugadores ? (
                        <p className="py-2 text-center text-sm text-muted-foreground">Cargando...</p>
                      ) : (
                        <JugadoresDetalle jugadores={jugadores} onDescuento={(jId, nombre) => openPromoDialog(jId, nombre, key)} onAplicarSaldo={(j) => openSaldoDialog(j, key)} onRetirarSaldo={(j) => handleRetirarSaldo(j, key)} onDeshacerAplicacion={(j, ap) => handleDeshacerAplicacion(j, ap, key)} />
                      )}
                    </div>
                  )}
                </Card>
              )
            })}
          </>
        )}
      </div>

      {/* Asignar descuento */}
      <Dialog open={saldoDialog.open} onOpenChange={(v) => { if (!v && !aplicandoSaldo) setSaldoDialog({ open: false, jugador: null, contextKey: null }) }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aplicar saldo a favor</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {saldoDialog.jugador?.nombre} tiene{' '}
              <strong>{formatMoney(saldoDialog.jugador?.saldoFavor || 0)}</strong> a su favor.
              Elige a qué cargo aplicarlo y cuánto.
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs">Cargo</Label>
              <Select
                value={cargoElegido}
                onValueChange={(val) => {
                  setCargoElegido(val)
                  const c = (saldoDialog.jugador?.cargos || []).find((x) => x.id === val)
                  const tope = Math.min(Number(c?.saldo || 0), Number(saldoDialog.jugador?.saldoFavor || 0))
                  setMontoAplicar(tope > 0 ? String(tope) : '')
                }}
              >
                <SelectTrigger><SelectValue placeholder="Seleccionar cargo" /></SelectTrigger>
                <SelectContent>
                  {(saldoDialog.jugador?.cargos || []).filter((c) => Number(c.saldo) > 0).map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {(c.pagos_planes_parcialidades?.nombre || c.pagos_conceptos?.nombre || c.notas || 'Cargo')} — debe {formatMoney(c.saldo)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Monto a aplicar</Label>
              <Input type="number" min="0" step="0.01" value={montoAplicar}
                onChange={(e) => setMontoAplicar(e.target.value)} placeholder="$0" />
              <p className="text-xs text-muted-foreground">
                Se propone lo que cubre el cargo sin pasarse de su saldo a favor.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaldoDialog({ open: false, jugador: null, contextKey: null })} disabled={aplicandoSaldo}>
              Cancelar
            </Button>
            <Button onClick={confirmarAplicarSaldo} disabled={aplicandoSaldo || !cargoElegido || !(Number(montoAplicar) > 0)}>
              {aplicandoSaldo ? 'Aplicando...' : 'Aplicar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promoDialog.open} onOpenChange={guard.onOpenChange}>
        <DialogContent {...guard.dirtyProps} className="max-w-sm">
          <DialogHeader><DialogTitle>Asignar descuento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Asignar una promoción a <strong>{promoDialog.jugadorNombre}</strong></p>
            {promosAplicables.length === 0 ? (
              <p className="py-4 text-center text-sm text-muted-foreground">No hay promociones aplicables para este jugador en esta temporada.</p>
            ) : (
              <div className="space-y-2">
                <Label>Promoción</Label>
                <Select value={selectedPromoId} onValueChange={(v) => { guard.markDirty(); setSelectedPromoId(v) }}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar promoción" /></SelectTrigger>
                  <SelectContent>
                    {promosAplicables.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nombre} {p.porcentaje ? `(${p.porcentaje}%)` : ''} — {p.tipo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => guard.onOpenChange(false)}>Cancelar</Button>
            <Button onClick={handleAsignarPromo} disabled={!selectedPromoId || assigningPromo}>
              {assigningPromo ? 'Asignando...' : 'Asignar'}
            </Button>
          </DialogFooter>
          {guard.DiscardDialog}
        </DialogContent>
      </Dialog>

      {/* Compartir */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Compartir Estado de Cuenta</DialogTitle></DialogHeader>
          {shareFamilia && (
            <div className="space-y-4">
              <p className="text-sm">
                <span className="text-muted-foreground">Cuenta: </span>
                <span className="font-medium">{shareFamilia.nombre}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                El tutor necesita <strong className="text-foreground">dos cosas</strong> para entrar: el <strong className="text-foreground">link</strong> y su <strong className="text-foreground">PIN</strong>.
              </p>

              {(() => {
                const lockUntil = shareFamilia.pin_bloqueado_hasta ? new Date(shareFamilia.pin_bloqueado_hasta) : null
                if (!lockUntil || lockUntil <= new Date()) return null
                return (
                  <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <p>Esta familia está <strong>bloqueada</strong> por intentos fallidos hasta las {lockUntil.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}. El tutor no podrá entrar aunque el PIN sea correcto.</p>
                      <Button size="sm" variant="outline" onClick={handleDesbloquear} disabled={unlocking}>
                        {unlocking ? 'Desbloqueando...' : 'Desbloquear ahora'}
                      </Button>
                    </div>
                  </div>
                )
              })()}

              <div className="space-y-2 rounded-lg border p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1 · Link de acceso</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => copyLink(shareFamilia)}>
                    <Copy className="mr-1.5 h-4 w-4" /> {copied ? 'Copiado' : 'Copiar'}
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-green-700" onClick={() => shareWhatsApp(shareFamilia)}>
                    <MessageCircle className="mr-1.5 h-4 w-4" /> WhatsApp
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-0 text-xs text-muted-foreground hover:text-destructive"
                  onClick={handleRevocarLink}
                  disabled={revocandoLink}
                >
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  {revocandoLink ? 'Revocando...' : 'Revocar el link actual y generar otro'}
                </Button>
              </div>

              <div className="space-y-2 rounded-lg border p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2 · PIN</p>
                {generatedPin ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-2xl font-bold tracking-widest text-foreground">{generatedPin}</span>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={copyPin} title="Copiar PIN">
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-[11px] font-medium text-amber-600">
                      {pinCopied ? 'PIN copiado. ' : ''}Anótalo o entrégalo ahora — por seguridad no se vuelve a mostrar.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Por seguridad el PIN no se puede consultar. Si el tutor ya tiene el suyo, úsalo tal cual. Para uno nuevo (reemplaza el anterior):
                    </p>
                    <Button variant="outline" size="sm" onClick={handleGenerarPin} disabled={regenPin}>
                      {regenPin ? 'Generando...' : 'Generar PIN nuevo'}
                    </Button>
                  </div>
                )}
              </div>

              <p className="text-[11px] text-muted-foreground">
                Tip: comparte el link y el PIN por separado (el PIN en persona o en otro mensaje).
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  )
}

export default EstadoCuentaTab
